/**
 * Tech Support Co-Pilot turn model + the SSE-event reducer.
 *
 * Kept in a sibling `.ts` (not the page `.tsx`) so it is pure + unit-testable and
 * so the page module exports only its component (the react-refresh ESLint rule).
 *
 * The Tech Support Co-Pilot renders the SAME block-by-block trace as the Agent
 * Console — its thinking, its tool calls, then its answer — by folding each
 * streamed `ConsoleEvent` into an `AssistantBlock[]` with the shared agentConsole
 * helpers. The voice rule mirrors the Quality Co-Pilot (SHARED helpers
 * `isVoiceReplyTool` / `spokenSummaryFromToolUse`): a `voice_reply` tool-use
 * becomes the spoken rundown (`spoken`) and is kept OUT of the block list, so it
 * never renders as a tool row and the answer renders normally.
 */
import {
  liftAgentFileCards,
  mergeAgentFiles,
  type AgentFileData,
} from '../components/agentConsole/agentFileCards';
import {
  AUTO_DRAFT_KICKOFF_TYPE,
  detailToTurns,
} from '../components/agentConsole/detailToTurns';
import type { MessageAttachment } from '../components/chat/messageAttachments';
import {
  appendLiveText,
  appendLiveThinking,
  applyLiveToolResult,
  toolCallViewFromUse,
  type AssistantBlock,
  type ConsoleEvent,
  type ConversationDetail,
} from '../services/agentConsole';
import { isVoiceReplyTool, spokenSummaryFromToolUse } from '../services/qualityCopilot';

/**
 * A reply the agent has DRAFTED into Gmail this turn (via its `create_draft` /
 * `update_draft` tool). Surfaced as a card in the chat transcript — mirroring the
 * inbox panel — so John sees the proposed email the moment the agent writes it,
 * without opening the trace. Read-only here; the human Send lives in the inbox
 * panel (the agent never sends).
 */
export interface DraftProposal {
  subject: string | null;
  body: string;
  to: string | null;
  /** Product documents the agent attached via attach_document_ids (count only —
   *  the inbox panel's draft card shows the actual files with download chips). */
  attachCount?: number;
}

/**
 * The faint system note that replaces the auto-draft's hidden kickoff message.
 * When the pipeline auto-drafts on a new email it seeds the turn with a system
 * instruction ("A new email came in…"); that is NOT John's words, so it renders
 * as this note instead of a user bubble. The message itself stays in the audit
 * trail (see AUTO_DRAFT_KICKOFF_TYPE / orchestrator.py).
 */
export const AUTO_DRAFT_KICKOFF_NOTE =
  'Auto-draft ran when this email arrived — Merlin reviewed the thread';

export type Turn =
  | {
      id: string;
      role: 'user';
      text: string;
      viaVoice?: boolean;
      // When the message happened — persisted created_at on resume, client
      // send time live. Shown on the bubble (browser-local convention).
      createdAt?: string | null;
      attachments?: MessageAttachment[];
    }
  | { id: string; role: 'system'; note: string; createdAt?: string | null }
  | {
      id: string;
      role: 'assistant';
      spoken: string | null; // voice_reply summary → playable rundown bubble
      blocks: AssistantBlock[]; // the thinking / tool / answer trace
      draft: DraftProposal | null; // a Gmail draft proposed this turn, if any
      streaming: boolean;
      // True when the user hit Stop mid-turn: the partial reply stays in the
      // thread with a calm "stopped" note (NOT an error) and the composer is
      // immediately ready for the next message in the same conversation.
      cancelled?: boolean;
      // Real audit ids for 👍/👎 feedback: the persisted assistant message id
      // (from the `done` frame live, or the saved row on resume) + the model used.
      messageId?: string | null;
      model?: string | null;
      createdAt?: string | null;
      /** Files the agent attached this turn (attach_file) — one card each. */
      agentFiles?: AgentFileData[];
    };

export type AssistantTurnModel = Extract<Turn, { role: 'assistant' }>;

// The email-thread the chat is bound to no longer rides the user's MESSAGE: the
// backend weaves the bound Gmail thread id into the kirk SYSTEM prompt (once per
// email-bound conversation, never in the General chat), so the page sends John's
// prompt verbatim and the persisted message stays clean. See the backend
// `InboxThreadContext` (services/agent_console/runtime/prompt.py).

// The agent's own Gmail draft tools (mounted as `mcp__gmail__*`). A use of either
// is what surfaces the proposed-reply card in the chat.
const GMAIL_DRAFT_TOOLS = new Set([
  'mcp__gmail__create_draft',
  'mcp__gmail__update_draft',
]);

/** Pull a {subject, body, to} draft out of a gmail draft tool call, or null. */
function draftFromInput(
  rawName: string | null | undefined,
  input: Record<string, unknown>,
): DraftProposal | null {
  if (!rawName || !GMAIL_DRAFT_TOOLS.has(rawName)) return null;
  const body = typeof input.body === 'string' ? input.body : '';
  if (!body.trim()) return null;
  const str = (v: unknown): string | null =>
    typeof v === 'string' && v.trim() ? v : null;
  const attachCount = Array.isArray(input.attach_document_ids)
    ? input.attach_document_ids.length
    : 0;
  return { subject: str(input.subject), body, to: str(input.to), attachCount };
}

/**
 * The canned follow-up the "Put this in the email draft" button sends as John's
 * next turn. It rides the normal send() path (persisted + audited like any user
 * message), and the agent — whose system prompt mandates that a revised reply
 * always lands in the Gmail draft — reads the current draft and writes this
 * response into it (update_draft, or create_draft when none exists). We ask the
 * AGENT to apply it rather than copying chat text into the draft ourselves
 * because the chat answer is a narration (bullets, judgment-call flags), not the
 * literal email body — only the agent can write the clean reply.
 */
export const APPLY_TO_DRAFT_PROMPT =
  'Put that response into the email draft — update the existing draft if there ' +
  'is one — so I can review, edit, and send it from the inbox.';

/**
 * Whether a turn should offer the "Put this in the email draft" recovery button:
 * the LAST settled assistant turn of an EMAIL-scoped chat that wrote an answer
 * but did NOT save a Gmail draft (the draft card already covers the turns that
 * did). This is the escape hatch for the failure John reported — Merlin revises
 * a reply in chat prose without pushing it into the editable draft.
 */
export function offerApplyToDraft(opts: {
  turn: Turn;
  isLast: boolean;
  emailScope: boolean;
  sending: boolean;
}): boolean {
  const { turn, isLast, emailScope, sending } = opts;
  if (!isLast || !emailScope || sending) return false;
  if (turn.role !== 'assistant' || turn.streaming || turn.draft) return false;
  // Only when there is a written answer to apply — a clarifying question spoken
  // via voice_reply (or a tool-only turn) has nothing to put in a draft.
  return turn.blocks.some((b) => b.kind === 'text' && b.text.trim().length > 0);
}

/**
 * Fold one streamed `ConsoleEvent` into an assistant turn, returning ONLY the
 * changed fields (pure — no TTS side effects; the page plays the rundown off the
 * same event via `makeRundownPlayer`). The stream-lifecycle frames
 * (state / done / error / cancelled) are handled by the page, not here.
 */
export function reduceTurn(
  turn: AssistantTurnModel,
  evt: ConsoleEvent,
): Partial<AssistantTurnModel> {
  const blocks = turn.blocks;
  switch (evt.type) {
    case 'text':
      return { blocks: appendLiveText(blocks, evt.text) };
    case 'thinking':
      return { blocks: appendLiveThinking(blocks, evt.text) };
    case 'tool_use':
      // The spoken rundown is its own layer, never a tool row in the trace. Keep
      // the latest non-empty summary so a re-streamed value can't blank it.
      if (isVoiceReplyTool(evt.name)) {
        const spoken = spokenSummaryFromToolUse(evt);
        return { spoken: spoken || turn.spoken };
      }
      {
        const next: Partial<AssistantTurnModel> = {
          blocks: [
            ...blocks,
            { kind: 'tool', tool: toolCallViewFromUse(evt, `live-${blocks.length}`) },
          ],
        };
        // A gmail draft tool also surfaces the proposed-reply card in the chat.
        // The latest draft wins (an update_draft supersedes its create_draft).
        const draft = draftFromInput(evt.name, evt.input ?? {});
        if (draft) next.draft = draft;
        return next;
      }
    case 'tool_result': {
      const applied = applyLiveToolResult(
        blocks,
        evt.tool_use_id,
        evt.is_error,
        evt.summary,
      );
      // A successful attach_file frame becomes a file card the moment its ack
      // lands — lifted out of the trace (pending/errored chips stay).
      const { blocks: kept, files } = liftAgentFileCards(applied);
      return {
        blocks: kept,
        ...(files.length
          ? { agentFiles: mergeAgentFiles(turn.agentFiles, files) }
          : {}),
      };
    }
    default:
      return {};
  }
}

/**
 * Rebuild a SAVED conversation into the Tech Support Co-Pilot's turn model so a
 * resumed thread renders identically to one just streamed. The shared
 * `detailToTurns` does the walk (block reconstruction + the voice rule); this
 * door carries no admin usage line, so the mapping onto its `Turn` is direct.
 */
export function detailToTechSupportTurns(detail: ConversationDetail): Turn[] {
  const turns = detailToTurns(detail).map<Turn>((t) => {
    if (t.role === 'user') {
      // The auto-draft's pipeline-typed kickoff is shown as a system note, not
      // as one of John's messages (the text stays in the audit trail).
      return t.kind === AUTO_DRAFT_KICKOFF_TYPE
        ? {
            id: t.id,
            role: 'system',
            note: AUTO_DRAFT_KICKOFF_NOTE,
            createdAt: t.createdAt ?? null,
          }
        : {
            id: t.id,
            role: 'user',
            text: t.text ?? '',
            createdAt: t.createdAt ?? null,
            attachments: t.attachments,
          };
    }
    return {
          id: t.id,
          role: 'assistant',
          spoken: t.spoken ?? null,
          blocks: t.blocks ?? [],
          draft: draftFromBlocks(t.blocks ?? []),
          streaming: false,
          // Resume carries the real persisted ids so feedback works on reload too.
          messageId: t.message?.id ?? null,
          model: t.message?.model ?? null,
          createdAt: t.createdAt ?? null,
          // Agent-file cards come pre-lifted from the shared detail walk.
          agentFiles: t.agentFiles,
        };
  });
  // A thread reloaded in the `cancelled` state ended with a user Stop: label the
  // last assistant turn so its (possibly partial) reply reads as interrupted on
  // resume, same as it did live. The state is conversation-level and resets to
  // `running` on the next turn, so only the trailing turn can be the stopped one.
  if (detail.conversation.state === 'cancelled') {
    for (let i = turns.length - 1; i >= 0; i -= 1) {
      const t = turns[i];
      if (t.role === 'assistant') {
        turns[i] = { ...t, cancelled: true };
        break;
      }
    }
  }
  return turns;
}

/**
 * Reconstruct the proposed-draft card for a RESUMED turn by scanning its tool
 * blocks for a gmail draft call (the saved `ToolCallView` keeps `rawName` +
 * `input`). The last draft tool in the turn wins, so a revision (update_draft)
 * supersedes the original create_draft — same rule as the live reducer.
 */
function draftFromBlocks(blocks: AssistantBlock[]): DraftProposal | null {
  let draft: DraftProposal | null = null;
  for (const block of blocks) {
    if (block.kind !== 'tool') continue;
    const found = draftFromInput(block.tool.rawName, block.tool.input);
    if (found) draft = found;
  }
  return draft;
}
